// angular dependencies
import { Injectable, inject } from '@angular/core';
import {
  AppRouterPaths,
  CoreRouterActions,
  RouterSelectors,
} from '@fantaz/core/router';

// third-party dependencies
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

// fantaz dependencies
import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { CoreSnackbarActions } from '@fantaz/core/state';
import { environment } from '@fantaz/shared/app-config';
import {
  AuthServiceInjectionToken,
  DataAccessBrowserService,
  UserService,
} from '@fantaz/shared/data-access/api';
import {
  LoadUserGoogleAnalyticEvent,
  LogoutUserGoogleAnalyticEvent,
} from '@fantaz/shared/data-access/models';
import { ColorSchemeService } from '@fantaz/shared/services/color-scheme';
import { AnnouncementBannerComponent } from '@fantaz/shared/ui/announcement-banner';
import { CurrencyService } from '@fantaz/shared/utils';
import { ViewService } from '@ngneat/overview';
import { forkJoin } from 'rxjs';
import {
  AuthActions,
  AuthAuthActions,
  AuthCurrentUserActions,
  AuthLocalStorageActions,
  AuthSessionActions,
  loadWalletSuccess,
} from './auth.actions';

@Injectable()
export class AuthEffects implements OnInitEffects {
  readonly #actions$ = inject(Actions);
  readonly #userService = inject(UserService);
  readonly #colorSchemeService = inject(ColorSchemeService);
  readonly #store = inject(Store);
  readonly #authService = inject(AuthServiceInjectionToken);
  readonly #appRouterPaths = inject(AppRouterPaths);
  readonly #browserService = inject(DataAccessBrowserService);
  readonly #viewService = inject(ViewService);
  readonly #currency = inject(CurrencyService);

  /**
   * Restore the authentication session from the local storage.
   */
  ngrxOnInitEffects(): Action {
    return AuthActions.init();
  }

  /**
   * Effect to init auth state. This effect is triggered when the `init` action
   * is dispatched. It checks if the user is logged in and if it is, it loads
   * the current session, user wallet and current user.
   */
  initEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthActions.init),
      switchMap(() =>
        this.#authService.isLoggedIn().pipe(
          mergeMap((isLoggedIn) => {
            if (isLoggedIn) {
              return [AuthActions.loadAuthSession()];
            }
            return [AuthActions.loaded({})];
          }),
        ),
      ),
    ),
  );

  /**
   * Effect to load the authentication session. This effect is triggered when the
   * `loadAuthSessionInProgress` action is dispatched. It loads the authentication
   * session and maps the result to the `loadAuthSessionSuccess` action. If an
   * error occurs, it maps the error to the `loadAuthSessionFailure` action.
   */
  loadAuthSessionEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthActions.loadAuthSession),
      switchMap(() =>
        forkJoin({
          session: this.#authService.getAuthSession(),
          currentUser: this.#authService.getCurrentUser(),
          wallet: this.#userService.getWallet(),
        }).pipe(
          mergeMap(({ session, currentUser, wallet }) => {
            return [
              AuthSessionActions.loadAuthSessionSuccess({
                session,
              }),
              AuthLocalStorageActions.saveUserSession(session),
              AuthCurrentUserActions.loadCurrentUserSuccess(currentUser),
              loadWalletSuccess({
                wallet,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  savingAuthSessionIntoLocalStorageEffect$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AuthLocalStorageActions.saveUserSession),
        tap(({ session }) => {
          this.#authService.saveIntoLocalStorage(session);
        }),
      ),
    { dispatch: false },
  );

  sendUserDataToGTMEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthCurrentUserActions.loadCurrentUserSuccess),
      filter(({ currentUser }) => !!currentUser?.user),
      map(({ currentUser }) =>
        CoreGoogleAnalyticActions.trackEvent({
          event: new LoadUserGoogleAnalyticEvent({
            user_id: currentUser?.user?.uuid,
            user_name: currentUser?.user.username || '',
            sponsor: currentUser?.user.sponsorUsername || '',
            env_name: environment.name,
          }),
        }),
      ),
    ),
  );

  /**
   * Effect that redirects the user to the URL specified in the query params or
   * to the home page if there is no redirect URL. This effect is triggered
   * when the sign-in process is successful.
   */
  redirectUserOnSignInSuccessEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthCurrentUserActions.loadCurrentUserSuccess),
      withLatestFrom(
        this.#store.select(RouterSelectors.selectQueryParam('redirectTo')),
        (_, redirectTo) => {
          const url = window.location.href;
          const r2 = localStorage.getItem('redirectTo');
          const r3 = new URLSearchParams(window.location.search).get(
            'redirectTo',
          );

          return {
            ..._,
            url,
            redirectTo: redirectTo || r2 || r3,
          };
        },
      ),
      switchMap(({ url, currentUser, redirectTo }) => {
        if (url.includes('/magic-link')) {
          return [];
        }

        /*
         * If user is authenticated in app browser then redirect to the magic
         * link page to continue the sign in process in another browser.
         */
        const isInApp = this.#browserService.isInApp();

        if (isInApp && (currentUser?.user?.email || currentUser?.user?.phone)) {
          const username = currentUser?.user?.email || currentUser?.user?.phone;
          const attrType = currentUser.user.email ? 'email' : 'phone_number';

          return [
            CoreRouterActions.navigate(
              [
                this.#appRouterPaths.resolve(
                  this.#appRouterPaths.routes.authSignInSendMagicLink,
                ).pathname,
              ],
              {
                queryParams: {},
                state: {
                  username,
                  attrType,
                  redirectTo,
                },
                queryParamsHandling: 'merge',
              },
            ),
          ];
        }

        //
        // Incomplete registration
        //
        if (
          !url?.includes('/get-started') &&
          !currentUser?.user?.registrationComplete
        ) {
          return [AuthAuthActions.continueToGetStarted()];
        }

        if (
          currentUser?.user?.walletFrozen &&
          currentUser?.user?.taxInfoCollectionStatus?.type === 'REQUIRED' &&
          !url?.includes('/profile') &&
          !url?.includes('/wallet') &&
          !url?.includes('/walletDisabled') &&
          !url?.includes('/tournaments')
        ) {
          return [
            CoreSnackbarActions.showSnackbar({
              message: `You're a great FantaZ earner! To keep on winning you'll need to fill out the tax form.`,
              config: {
                duration: 6000,
                dismissible: true,
              },
            }),
            CoreRouterActions.goToRoute('/walletDisabled', {
              external: true,
            }),
          ];
        }

        // in case restore session from any route
        if (!url.includes('/auth')) {
          return [];
        }

        const playPage =
          currentUser.user.userSettings?.playPage || '/tournaments';

        return redirectTo && !['', '/'].includes(redirectTo as string)
          ? [
              CoreRouterActions.redirectTo(
                sanitizeRedirectTo(redirectTo as string),
              ),
            ]
          : [
              CoreRouterActions.goToRoute(sanitizeRedirectTo(playPage), {
                external: true,
              }),
            ];
      }),
    ),
  );

  logoutEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthAuthActions.logout),
      switchMap(() =>
        this.#authService.signOut().pipe(
          concatMap(() => [
            AuthAuthActions.logoutSuccess(),
            CoreGoogleAnalyticActions.trackEvent({
              event: new LogoutUserGoogleAnalyticEvent({
                env_name: environment.name,
              }),
            }),
            CoreRouterActions.goToRoute('/', {
              external: true,
            }),
          ]),
        ),
      ),
    ),
  );

  continueToGetStartedEffect$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(AuthAuthActions.continueToGetStarted),
      filter(() => !window.location.href.includes('/get-started')),
      map(() => {
        const redirectTo = getRedirectTo();
        const url = this.#appRouterPaths.resolve(
          this.#appRouterPaths.routes.getStarted,
        );

        if (redirectTo) {
          url.searchParams.append('redirectTo', redirectTo);
        } else {
          if (!window.location.href.includes('/auth')) {
            url.searchParams.append('redirectTo', window.location.href);
          }
        }

        return CoreRouterActions.redirectTo(url.toString());
      }),
    ),
  );

  /**
   * Effect to show a pending loan announcement if the user has a pending loan
   * or a negative balance. This effect is triggered when the wallet is loaded
   * successfully.
   * Only shows the announcement if the user is not on certain routes
   */
  showWalletPendingLoanAnnouncementEffect$ = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(loadWalletSuccess),
        withLatestFrom(this.#store.select(RouterSelectors.selectFragment)),

        /*
         * Filter to only show the announcement on specific routes
         * Not visible on a: /auth, /get-started, /join, /game-wrapper
         */
        filter(([_, fragment]) => {
          return !['/auth', '/get-started', '/join', '/game-wrapper'].some(
            (p) => fragment?.includes(p),
          );
        }),

        /*
         * Show the announcement banner if the wallet has a negative balance or
         * user have a pending loan
         */
        switchMap(([{ wallet }]) => {
          if (
            (wallet && wallet?.loanBalance && wallet.loanBalance > 0) ||
            (wallet?.balance && wallet?.balance < 0)
          ) {
            if (wallet?.balance && wallet?.balance < 0) {
              const balance = this.#currency.convertDollar(wallet.balance);
              this.#viewService
                .createComponent(AnnouncementBannerComponent, {
                  context: {
                    severity: 'alert',
                    content: `Your wallet’s balance is negative: <b>${balance} USD</b>. Please <a href="/wallet/deposit?pay-negative-balance=${wallet.balance}">make a deposit</a> to continue playing in your favorite tournaments.`,
                  },
                })
                .detectChanges()
                .appendTo(document.body);
            }
          }
          return [];
        }),
      ),
    {
      dispatch: false,
    },
  );
}

function getRedirectTo() {
  const searchParams = new URLSearchParams(window.location.search);
  const localStorageRedirectTo = localStorage.getItem('redirectTo');
  return searchParams.get('redirectTo') || localStorageRedirectTo;
}

function sanitizeRedirectTo(route: string): string {
  if (!route.startsWith('/')) {
    return `/${route}`;
  }

  return route;
}
